// /accept-invite?token=... - landing target for invited users.
//
// Flow:
//   1. Email link -> /accept-invite?token=<uuid>
//   2. Not signed in -> /login?invite=<token> (LoginPage carries the token
//      through OAuth via /auth/callback?next=).
//   3. Signed in -> call accept_invitation(token). AuthProvider also
//      auto-claims pending invites for this email on login, so by the time
//      we get here the membership is often already attached.
//   4. Either way, welcome the authenticated user and send them in. The
//      console's routing (hasOrg ? console : NoOrgAccess) places them, so a
//      redundant/expired/wrong-email invite is never a dead end.

import { useEffect, useState } from 'react';
import { supabase, useAuth } from '../index.js';

export default function AcceptInvitePage() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [phase, setPhase] = useState('working'); // working | welcome
  const [orgName, setOrgName] = useState('');
  // G360_K375 - what actually happened, so the page stops welcoming
  // everybody regardless. Empty means say nothing, which is correct for
  // an expired or unknown token: the user is signed in either way.
  const [outcome, setOutcome] = useState('');

  const token = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  ).get('token');

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      const q = token ? `?invite=${encodeURIComponent(token)}` : '';
      window.location.replace(`/login${q}`);
      return;
    }

    let active = true;
    (async () => {
      if (token) {
        // Attempt to accept. If it fails (wrong email / expired / already
        // used / already auto-claimed) we do NOT block - the user is signed
        // in and routing will place them.
        const { data } = await supabase.rpc('accept_invitation', {
          invitation_token: token,
        });
        /* G360_K375 - accept_invitation returns a TABLE, so this is an
           ARRAY. Reading the name field off the array itself was always
           undefined, so the heading never carried the org name. */
        const row = Array.isArray(data) ? data[0] : data;
        if (active && row) {
          if (row.tenant_name) setOrgName(row.tenant_name);
          if (row.outcome) setOutcome(row.outcome);
        }
      }
      if (active) setPhase('welcome');
    })();

    return () => { active = false; };
  }, [authLoading, isAuthenticated, token, user?.id]);

  /* G360_K375 - one line per outcome. Nothing here blocks or hides the
     Continue button: the user is authenticated and the console routes
     them on hasOrg, so an invitation that did not apply must not be a
     dead end. The only thing that changes is whether this page claims
     something that did not happen. */
  function heading() {
    if (outcome === 'already_member' || outcome === 'already_accepted') {
      return orgName ? ('You are already part of ' + orgName) : 'You already have access';
    }
    if (outcome === 'wrong_email') return 'That invitation was sent to a different address';
    return 'Welcome to Govern360' + (orgName ? ', ' + orgName : '');
  }

  function subline() {
    if (outcome === 'wrong_email') {
      // The one refusal with an obvious remedy, so it is worth stating.
      return 'You are signed in as a different account. To use that invitation, '
        + 'sign in with the address it was sent to. Otherwise carry on below.';
    }
    if (outcome === 'already_member' || outcome === 'already_accepted') {
      return 'Nothing has changed - your existing access and role are unaffected.';
    }
    return 'This is where your AI governance starts. Discover where AI is used across your '
      + 'organization, set the policies that keep it safe, and produce audit-ready '
      + 'compliance evidence - all in one place.';
  }

  function go() { window.location.replace('/'); }

  return (
    <div className="login-wrap">
      <div className="login-card" style={{ textAlign: 'center', maxWidth: 460 }}>
        {phase === 'working' ? (
          <>
            <div
              className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[var(--g-primary)] border-t-transparent"
              style={{ margin: '0 auto' }}
            />
            <p style={{ marginTop: 16, fontSize: 14, color: 'var(--g-text-2)' }}>Setting up your access...</p>
          </>
        ) : (
          <>
            <h1 className="login-h1" style={{ marginBottom: 8 }}>
              {heading()}{/* G360_K375 */}
            </h1>
            <p className="login-sub" style={{ marginBottom: 4 }}>
              {subline()}


            </p>
            <button
              className="btn primary block"
              style={{ marginTop: 20, height: 44 }}
              onClick={go}
            >
              Continue to your console
            </button>
          </>
        )}
      </div>
    </div>
  );
}